import { useEffect } from 'react';

import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import { isWholeSalerWidget } from '@/lib/const';
import { EVENT_NAME, GA_PAGE_VIEW_DELAY_MS } from '@/lib/gtm/const';
import { getGTMEventQueue } from '@/lib/gtm/eventQueue';
import type { WithChildren } from '@/lib/types';
import { useGtmEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';
import {
  pageReferrerStateActions,
  usePageReferrerState,
} from '@/states/pageReferrerState';

export const GoogleAnalyticsProvider = ({ children }: WithChildren) => {
  const { asPath, pathname } = useRouter();
  const { sendEvent } = useGoogleAnalytics();
  const { pageReferrer } = usePageReferrerState();
  const isGtmEnabled = useGtmEnabled();

  useEffect(() => {
    if (!isGtmEnabled) return;
    window._uxa = window._uxa || [];
    window._uxa.push(['setPIISelectors', { PIISelectors: ['.pii'] }]);
  }, [isGtmEnabled]);

  useEffect(() => {
    if (pathname === '/404' || isWholeSalerWidget(pathname)) return;
    if (!isGtmEnabled) return;

    // Reset queue for new page navigation
    const queue = getGTMEventQueue();
    queue.reset();

    // Delay GA4 event to allow user authentication and metadata to resolve
    const timeoutId = setTimeout(() => {
      sendEvent({
        event: EVENT_NAME.PAGE_VIEW,
        page_referrer: pageReferrer || document.referrer,
      });
      // Note: page_view event automatically marks queue as initialized
    }, GA_PAGE_VIEW_DELAY_MS);

    // Update referrer state immediately (no delay needed)
    pageReferrerStateActions.setPageReferrer(window.location.href);
    pageReferrerStateActions.setPrevPageReferrer(pageReferrer);

    return () => clearTimeout(timeoutId);
  }, [pathname, asPath, pageReferrer, isGtmEnabled]);

  return <>{children}</>;
};
